import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { FilterLinesIcon, XCloseIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from '@/components/application/side-panel'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormCheckbox } from '@/components/forms/FormCheckbox'
import type { WhatsAppRegistrationStatus } from '@/hooks/api'

export interface PhoneNumberFilterValues {
  q?: string
  whatsapp_status?: WhatsAppRegistrationStatus | 'none'
  twilio_only?: boolean
}

interface Props {
  filters: PhoneNumberFilterValues
  onFiltersChange: (filters: PhoneNumberFilterValues) => void
}

interface FormValues {
  whatsapp_status: string
  twilio_only: boolean
}

export function PhoneNumberFilters({ filters, onFiltersChange }: Props) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const methods = useForm<FormValues>({
    defaultValues: {
      whatsapp_status: filters.whatsapp_status ?? '',
      twilio_only: filters.twilio_only ?? false,
    },
  })

  useEffect(() => {
    methods.reset({
      whatsapp_status: filters.whatsapp_status ?? '',
      twilio_only: filters.twilio_only ?? false,
    })
  }, [filters, methods])

  const statusOptions = [
    { value: '', label: t('resources.phone_numbers.wa_status_all') },
    { value: 'online', label: t('resources.phone_numbers.wa_status_online') },
    { value: 'pending', label: t('resources.phone_numbers.wa_status_pending') },
    { value: 'offline', label: t('resources.phone_numbers.wa_status_offline') },
    { value: 'failed', label: t('resources.phone_numbers.wa_status_failed') },
    { value: 'none', label: t('resources.phone_numbers.wa_status_none') },
  ]

  const handleApply = useCallback((values: FormValues) => {
    const next: PhoneNumberFilterValues = { q: filters.q }
    if (values.whatsapp_status) next.whatsapp_status = values.whatsapp_status as PhoneNumberFilterValues['whatsapp_status']
    if (values.twilio_only) next.twilio_only = true
    onFiltersChange(next)
    setIsOpen(false)
  }, [filters.q, onFiltersChange])

  const handleClear = useCallback(() => {
    methods.reset({ whatsapp_status: '', twilio_only: false })
    onFiltersChange({ q: filters.q })
    setIsOpen(false)
  }, [filters.q, methods, onFiltersChange])

  const handleQuickClear = useCallback(() => {
    onFiltersChange({ q: filters.q })
  }, [filters.q, onFiltersChange])

  const activeFilterCount =
    (filters.whatsapp_status ? 1 : 0) + (filters.twilio_only ? 1 : 0)

  return (
    <>
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xs dark:border-gray-600 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <FilterLinesIcon className="h-4 w-4" />
          {t('resources.generic.filters')}
          {activeFilterCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-xs font-medium text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
            <button
              type="button"
              onClick={handleQuickClear}
              className="flex items-center justify-center px-2 py-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              title={t('resources.generic.clear_all')}
            >
              <XCloseIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <SidePanel
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
        side="right"
        size="md"
        variant="filter"
      >
        <SidePanelContent>
          <SidePanelHeader
            title={t('resources.generic.filters')}
            onClose={() => setIsOpen(false)}
          />
          <SidePanelBody>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(handleApply)}
                className="space-y-6"
              >
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.phone_numbers.filter_whatsapp_status')}
                  </label>
                  <FormSelect
                    name="whatsapp_status"
                    control={methods.control}
                    options={statusOptions}
                  />
                </div>

                <FormCheckbox
                  name="twilio_only"
                  control={methods.control}
                  label={t('resources.phone_numbers.twilio_only')}
                />
              </form>
            </FormProvider>
          </SidePanelBody>
          <SidePanelFooter>
            <Button color="secondary" onPress={handleClear}>
              {t('resources.generic.clear_all')}
            </Button>
            <Button color="primary" onPress={methods.handleSubmit(handleApply)}>
              {t('resources.generic.apply_filters')}
            </Button>
          </SidePanelFooter>
        </SidePanelContent>
      </SidePanel>
    </>
  )
}
